import { useCallback, useRef } from 'react';
import { calculateQuoteEngine, createQuote, updateQuote, validateQuoteEngine, saveQuoteLineOrder, saveCompositionInformation, getQuoteVersion } from '../quote.api';
import { buildQuotePayload, buildQuoteCommercialPayload, editorFromQuote, isLineOrderOnlyEdit } from '../quoteEditorModel';
import { adaptQuoteEngineResponse } from '../quoteEngineContract';
import { isQuoteRevision, localChangesDuringWrite } from '../quoteRevision';

export default function useQuoteSave({ editor, calculationRequest, setSaving, setError, setEditor,
  setStatusMessage, loadFolder, loadQuotes, reportRevisionError, composition }) {
  const currentEditor = useRef(editor);
  currentEditor.current = editor;
  return useCallback(async () => {
    if (editor?.serverWriteConflict) {
      setError('Conservez vos saisies puis rechargez explicitement la version après la commande serveur avant de sauvegarder.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      let saved;
      if (editor.contentSchemaVersion === 2) {
        const commercial = buildQuoteCommercialPayload(editor);
        const information = { ...commercial.versionInformation };
        if (editor.permissions?.canViewCosts !== false) information.defaultMarginCoefficient = editor.defaultMarginCoefficient;
        if (editor.permissions?.canViewCosts === false) delete information.notesInternal;
        await saveCompositionInformation(editor.quoteVersionId, { folder: { title: editor.title }, information,
          compositionChanges: composition?.pendingMutations || [],
          paymentSchedule: editor.paymentSchedule || [],
          ...(editor.permissions?.canViewCosts === false ? {} : { parameters: editor.versionParameters || [], expenses: editor.versionExpenses || [] }) }, editor.revision);
        saved = await getQuoteVersion(editor.id, editor.quoteVersionId);
      } else if (isLineOrderOnlyEdit(editor)) {
        saved = await saveQuoteLineOrder(editor.id, editor.quoteVersionId, editor.sections, editor.revision);
      } else {
        let payload;
        if (editor.permissions?.canViewCosts === false) {
          payload = buildQuoteCommercialPayload(editor);
        } else {
          if (!calculationRequest) throw new Error('Le contrat de calcul du devis est indisponible.');
          await validateQuoteEngine(calculationRequest);
          const engineResponse = await calculateQuoteEngine(calculationRequest);
          const calculation = adaptQuoteEngineResponse(engineResponse, editor);
          payload = buildQuotePayload(editor, calculation);
        }
        saved = editor.id ? await updateQuote(editor.id, editor.quoteVersionId, payload) : await createQuote(payload);
      }
      if (currentEditor.current !== editor) {
        const versionId = saved.quoteVersionId ?? saved.version?.id;
        if (!editor.id && saved.id && !currentEditor.current?.id) {
          // Keep the new server identity to prevent duplicate creation. A null
          // revision blocks another write until the persistent rows are loaded.
          setEditor((current) => ({ ...current, id: saved.id, quoteVersionId: versionId,
            revision: null, serverCalculationStatus: 'stale' }));
        }
        throw localChangesDuringWrite({ quoteId: saved.id, quoteVersionId: versionId });
      }
      const nextEditor = editorFromQuote(saved);
      if (nextEditor.revision == null && isQuoteRevision(saved._revision)) nextEditor.revision = saved._revision;
      setEditor(nextEditor);
      composition?.clearPending();
      if (saved.id) await loadFolder(saved.id);
      await loadQuotes();
      setStatusMessage(`Devis ${saved.quoteNumber || saved.quote_number} enregistré.`);
    } catch (error) {
      reportRevisionError(error, 'Impossible d’enregistrer le devis');
    } finally { setSaving(false); }
  }, [editor, calculationRequest, setSaving, setError, setEditor, setStatusMessage, loadFolder, loadQuotes, reportRevisionError, composition]);
}
